import { z } from 'zod';

import { api } from '@shared/api';
import { createQuery } from '@shared/lib/query-helpers';

import {
  crmCustomerProfileBusinessLinesSchema,
  crmCustomerProfileMainLocationsSchema,
  crmCustomerProfileMutationStateSchema,
  crmCustomerProfileSexesSchema,
  crmCustomerProfileStatusesSchema,
  type CrmCustomerProfileBusinessLinesResponse,
  type CrmCustomerProfileMainLocationsResponse,
  type CrmCustomerProfileResponse,
  type CrmCustomerProfileSexesResponse,
  type CrmCustomerProfileStatusesResponse,
} from './schemas';

export const crmCustomerProfileMutationStateMediaType =
  'application/vnd.molchanovs.user-profile-mutation-state.v1+json';

export const crmCustomerProfileKeys = {
  all: ['crm-customer-profile'] as const,
  languages: (viewerAccessScope: string) =>
    [...crmCustomerProfileKeys.all, 'language-options', viewerAccessScope] as const,
  businessLines: (viewerAccessScope: string) =>
    [...crmCustomerProfileKeys.all, 'business-lines', viewerAccessScope] as const,
  detail: (viewerAccessScope: string, customerId: string) =>
    [...crmCustomerProfileKeys.all, viewerAccessScope, customerId] as const,
  mainLocations: (viewerAccessScope: string, search: string, selected: string | null) =>
    [
      ...crmCustomerProfileKeys.all,
      'main-locations',
      viewerAccessScope,
      search,
      selected ?? '',
    ] as const,
  mutationState: (viewerAccessScope: string, customerId: string) =>
    [...crmCustomerProfileKeys.all, 'mutation-state', viewerAccessScope, customerId] as const,
  sexes: (viewerAccessScope: string) =>
    [...crmCustomerProfileKeys.all, 'sexes', viewerAccessScope] as const,
  statuses: (viewerAccessScope: string) =>
    [...crmCustomerProfileKeys.all, 'statuses', viewerAccessScope] as const,
};

export function crmCustomerProfileBusinessLinesQuery(viewerAccessScope: string, enabled = true) {
  return createQuery<CrmCustomerProfileBusinessLinesResponse>({
    enabled,
    path: '/api/v1/business-lines',
    queryKey: crmCustomerProfileKeys.businessLines(viewerAccessScope),
    schema: crmCustomerProfileBusinessLinesSchema,
  });
}

export function crmCustomerProfileQuery(viewerAccessScope: string, customerId: string) {
  return createQuery<CrmCustomerProfileResponse>({
    path: `/api/v1/user-profiles/${encodeURIComponent(customerId)}`,
    queryKey: crmCustomerProfileKeys.detail(viewerAccessScope, customerId),
  });
}

export function crmCustomerProfileSexesQuery(viewerAccessScope: string, enabled = true) {
  return createQuery<CrmCustomerProfileSexesResponse>({
    enabled,
    path: '/api/v1/user-profiles/sexes',
    queryKey: crmCustomerProfileKeys.sexes(viewerAccessScope),
    schema: crmCustomerProfileSexesSchema,
  });
}

export function crmCustomerProfileStatusesQuery(viewerAccessScope: string, enabled = true) {
  return createQuery<CrmCustomerProfileStatusesResponse>({
    enabled,
    path: '/api/v1/user-profiles/statuses',
    queryKey: crmCustomerProfileKeys.statuses(viewerAccessScope),
    schema: crmCustomerProfileStatusesSchema,
  });
}

export function crmCustomerProfileMainLocationsQuery(
  viewerAccessScope: string,
  search: string,
  selected: string | null,
  enabled = true
) {
  return createQuery<CrmCustomerProfileMainLocationsResponse>({
    enabled,
    path: '/api/v1/user-profiles/main-locations',
    queryKey: crmCustomerProfileKeys.mainLocations(viewerAccessScope, search, selected),
    schema: crmCustomerProfileMainLocationsSchema,
    requestOptions: {
      params: {
        ...(search.trim().length >= 2 ? { q: search.trim() } : {}),
        ...(selected ? { selected: [selected] } : {}),
      },
    },
  });
}

export async function getCrmCustomerProfileMutationState(customerId: string, signal?: AbortSignal) {
  return api.getWithMetadata(`/api/v1/user-profiles/${encodeURIComponent(customerId)}`, {
    headers: { Accept: crmCustomerProfileMutationStateMediaType },
    schema: crmCustomerProfileMutationStateSchema,
    signal,
  });
}

const languageOptionsSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        accessId: z.uuid(),
        display: z.string().nullable(),
        appLocaleCode: z.string().nullable(),
      })
    ),
  }),
  meta: z.object({
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }),
});

export function crmCustomerProfileLanguagesQuery(viewerAccessScope: string) {
  return {
    queryKey: crmCustomerProfileKeys.languages(viewerAccessScope),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const items: z.infer<typeof languageOptionsSchema>['data']['items'] = [];
      let totalPages = 1;
      let totalItems: number | undefined;
      const seen = new Set<string>();
      for (let page = 1; page <= totalPages; page += 1) {
        const response = await api.get('/api/v1/localization/languages', {
          params: { showForUsers: true, page, pageSize: 25 },
          schema: languageOptionsSchema,
          signal,
        });
        totalItems ??= response.meta.totalItems;
        totalPages = Math.max(1, Math.ceil(totalItems / 25));
        if (
          response.meta.page !== page ||
          response.meta.pageSize !== 25 ||
          response.meta.totalItems !== totalItems ||
          response.meta.totalPages !== Math.ceil(totalItems / 25) ||
          response.data.items.length !== Math.min(25, totalItems - items.length) ||
          response.data.items.some((item) => seen.has(item.accessId)) ||
          new Set(response.data.items.map((item) => item.accessId)).size !==
            response.data.items.length
        ) {
          throw new Error('Inconsistent language option pagination');
        }
        for (const item of response.data.items) seen.add(item.accessId);
        items.push(...response.data.items);
      }
      return items;
    },
  };
}
